import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { CloseCircleFilled, CloseOutlined } from '@ant-design/icons';
import { Button, DatePicker, Input, Popover, Radio } from 'antd';
import ContentLayout from '../../users/components/ContentLayout';
import TableSection from '../../users/components/TableSection';
import {
  bulkRenewTherapies,
  exportTherapiesToExcel,
  fetchTherapies,
  fetchTherapySelection,
  fetchUserTherapyReservation,
  fetchUserTherapyModalContext,
  getTherapyModalContextFromResponse,
  getTherapyReservationFromResponse,
  mergeTherapyContextReservation,
} from '../api/therapiesApi';
import { updateTherapyOccurrenceNotification } from '../api/therapyOccurrencesApi';
import UpdateTherapyStatusModal from '../components/modals/UpdateTherapyStatusModal';
import SendTherapyReminderModal from '../components/modals/SendTherapyReminderModal';
import TherapyOccurrencesModal from '../components/modals/TherapyOccurrencesModal';
import RenewTherapyModal from '../components/modals/RenewTherapyModal';
import BulkRenewTherapiesModal from '../components/modals/BulkRenewTherapiesModal';
import ImportTherapiesModal from '../components/modals/ImportTherapiesModal';
import TherapiesExpandableTable from '../components/TherapiesExpandableTable';
import MedicationFilterSelect, {
  DEFAULT_MEDICATION_MATCH,
} from '../../medications/components/MedicationFilterSelect';
import AddTherapyModal from '../../users/components/modals/AddTherapyModal';
import { useAuth } from '../../../context/useAuth';
import { userPickupMethodOptions } from '../../users/constants/userConstants';
import ViewUserDetailsModal from '../components/modals/ViewUserDetailsModal';
import {
  fetchUserById,
  updateUser,
  updateUserPassword,
} from '../../users/api/usersApi';
import fixedBottomDatePickerProps from '../../../shared/utils/datePickerPlacement';
import PostalFormModal from '../../postalForm/components/PostalFormModal';
import usePostalForm from '../../postalForm/hooks/usePostalForm';
import useTableSearch from '../../../shared/hooks/useTableSearch';
import useToast from '../../../shared/hooks/useToast';
import { isAdmin, isStaff } from '../../../shared/utils/roles';
// Prikaz tabele terapija: 100 zapisa po stranici (defaultni prikaz).
const THERAPIES_PAGE_SIZE = 100;

const THERAPIES_EMPTY_STATE = {
  icon: '/assets/icons/no-users.svg',
  title: 'Trenutno nema terapija',
  description: 'Dodajte terapije korisnicima',
};

export default function TherapiesPage() {
  const { user } = useAuth();
  const messageApi = useToast();
  const postalForm = usePostalForm();
  const openPostalFormForUserId = postalForm.openForUserId;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Jedinstvena pretraga (Pacijent/LBO/BZK/Telefon) - src/config/searchConfig.js.
  const tableSearch = useTableSearch('therapies');
  const { reset: resetTableSearch } = tableSearch;
  const {
    userSearch: debouncedUserSearch,
    lboSearch: debouncedLboSearch,
    bzkSearch: debouncedBzkSearch,
    phoneSearch: debouncedPhoneSearch,
  } = tableSearch.params;
  const [selectedMedications, setSelectedMedications] = useState([]);
  const [medicationMatch, setMedicationMatch] = useState(
    DEFAULT_MEDICATION_MATCH
  );
  const [startDateSearch, setStartDateSearch] = useState('');
  const [debouncedStartDateSearch, setDebouncedStartDateSearch] = useState('');
  const [endDateSearch, setEndDateSearch] = useState('');
  const [debouncedEndDateSearch, setDebouncedEndDateSearch] = useState('');
  const [therapies, setTherapies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(null);
  const [listLoading, setListLoading] = useState(false);
  const [filter, setFilter] = useState({});
  const [message, setMessage] = useState('');
  const [selectedTherapy, setSelectedTherapy] = useState(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedTherapyForReminder, setSelectedTherapyForReminder] =
    useState(null);
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [selectedTherapyIds, setSelectedTherapyIds] = useState([]);
  const [selectedTherapiesById, setSelectedTherapiesById] = useState({});
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [bulkRenewLoading, setBulkRenewLoading] = useState(false);
  const [isBulkRenewModalOpen, setIsBulkRenewModalOpen] = useState(false);
  const [isImportTherapiesModalOpen, setIsImportTherapiesModalOpen] =
    useState(false);
  const [bulkRenewError, setBulkRenewError] = useState('');
  const [selectedTherapyForOccurrences, setSelectedTherapyForOccurrences] =
    useState(null);
  const [isOccurrencesModalOpen, setIsOccurrencesModalOpen] = useState(false);
  const [occurrencesLoadingTherapyId, setOccurrencesLoadingTherapyId] =
    useState(null);
  const [selectedTherapyForRenew, setSelectedTherapyForRenew] = useState(null);
  const [isRenewModalOpen, setIsRenewModalOpen] = useState(false);
  const [userForAcuteTherapyCreate, setUserForAcuteTherapyCreate] =
    useState(null);
  const [isAcuteTherapyCreateOpen, setIsAcuteTherapyCreateOpen] =
    useState(false);
  const [isViewUserDetailsModalOpen, setIsViewUserDetailsModalOpen] =
    useState(false);
  const [userForView, setUserForView] = useState(null);
  const [viewUserDetailsLoading, setViewUserDetailsLoading] = useState(false);
  const [refetchTherapies, setRefetchTherapies] = useState(false);
  const [notificationUpdatingIds, setNotificationUpdatingIds] = useState([]);
  const latestTherapiesRequestRef = useRef(0);
  const [renewStartDateSearch, setRenewStartDateSearch] = useState('');
  const [renewEndDateSearch, setRenewEndDateSearch] = useState('');
  const [userBirthdaySearch, setUserBirthdaySearch] = useState('');
  const [dateFilterMode, setDateFilterMode] = useState('activity');
  const [birthdayPickerOpen, setBirthdayPickerOpen] = useState(false);
  const [birthdayDraft, setBirthdayDraft] = useState({
    day: '',
    month: '',
    year: '',
  });
  const handleViewUserDetailsModalClose = () => {
    setIsViewUserDetailsModalOpen(false);
    setUserForView(null);
    setViewUserDetailsLoading(false);
  };

  const onRefetchTherapies = useCallback(() => {
    setRefetchTherapies((prev) => !prev);
  }, []);

  const loadTherapyContextWithReservation = useCallback(
    async (therapyContext) => {
      if (!therapyContext?.userId) return therapyContext || null;

      const { data, ok } = await fetchUserTherapyReservation(
        therapyContext.userId
      );

      const activeReservation = ok
        ? getTherapyReservationFromResponse(data)
        : null;

      return mergeTherapyContextReservation(
        therapyContext,
        activeReservation
      );
    },
    []
  );

  const handleViewUserDetails = useCallback(
    async (userId) => {
      setViewUserDetailsLoading(true);
      // return;
      try {
        const { data, ok, message } = await fetchUserById(userId);
        const userData =
          data?.user || data?.data?.user || data?.data || data || null;

        if (!ok || !userData) {
          setMessage(message || 'Greska pri ucitavanju korisnika');
          return;
        }

        setUserForView(userData);
        setIsViewUserDetailsModalOpen(true);
      } finally {
        setViewUserDetailsLoading(false);
      }
    },
    [setIsViewUserDetailsModalOpen, setUserForView, setViewUserDetailsLoading]
  );

  const handleRefreshUserForView = useCallback(async () => {
    if (!userForView?._id) return;

    const { data, ok } = await fetchUserById(userForView._id);
    const userData =
      data?.user || data?.data?.user || data?.data || data || null;

    if (ok && userData) {
      setUserForView(userData);
    }
  }, [userForView?._id]);

  const handleViewUserDetailsSubmit = useCallback(
    async (formData) => {
      if (!userForView?._id) {
        return { ok: false, message: 'Korisnik nije izabran.' };
      }

      setViewUserDetailsLoading(true);
      try {
        const { data, ok, message, status, code } = await updateUser(
          userForView._id,
          formData,
          userForView
        );

        if (!ok) {
          setMessage(message || 'Doslo je do greske pri azuriranju korisnika.');
          return { ok: false, message, status, code };
        }

        const updatedUser =
          data?.user || data?.data?.user || data?.data || data || null;

        if (updatedUser) {
          setUserForView(updatedUser);
        }

        setMessage(message || 'Korisnik uspesno azuriran.');
        onRefetchTherapies();
        return { ok: true, message: message || 'Korisnik uspesno azuriran.' };
      } finally {
        setViewUserDetailsLoading(false);
      }
    },
    [onRefetchTherapies, userForView]
  );

  const handleViewUserPasswordSubmit = useCallback(
    async (password) => {
      if (!userForView?._id) {
        return { ok: false, message: 'Korisnik nije izabran.' };
      }

      setViewUserDetailsLoading(true);
      try {
        const { data, ok, message } = await updateUserPassword(
          userForView._id,
          password
        );

        if (!ok) {
          setMessage(message || 'Doslo je do greske pri azuriranju lozinke korisnika.');
          return { ok: false, message };
        }

        const updatedUser =
          data?.user || data?.data?.user || data?.data || data || null;

        if (updatedUser) {
          setUserForView(updatedUser);
        }

        setMessage(message || 'Lozinka korisnika uspesno azurirana.');
        onRefetchTherapies();
        return {
          ok: true,
          message: message || 'Lozinka korisnika uspesno azurirana.',
        };
      } finally {
        setViewUserDetailsLoading(false);
      }
    },
    [onRefetchTherapies, userForView]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedStartDateSearch(startDateSearch),
      500
    );
    return () => clearTimeout(timer);
  }, [startDateSearch]);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedEndDateSearch(endDateSearch),
      500
    );
    return () => clearTimeout(timer);
  }, [endDateSearch]);

  const searchResetSkipRef = useRef(true);
  useEffect(() => {
    if (searchResetSkipRef.current) {
      searchResetSkipRef.current = false;
      return;
    }
    // Kada se pretraga/filter promeni, vrati se na prvu stranicu da pretraga
    // gleda ceo (filtrirani) skup, a ne da ostane na staroj stranici.
    setCurrentPage(1);
  }, [
    debouncedSearch,
    debouncedUserSearch,
    selectedMedications,
    medicationMatch,
    debouncedLboSearch,
    debouncedBzkSearch,
    debouncedPhoneSearch,
    debouncedStartDateSearch,
    debouncedEndDateSearch,
    renewStartDateSearch,
    renewEndDateSearch,
    userBirthdaySearch,
    dateFilterMode,
    filter,
  ]);

  const getCurrentTherapyFilters = useCallback(
    (page = currentPage, limit = THERAPIES_PAGE_SIZE) => ({
      page,
      limit,
      search: debouncedSearch,
      userSearch: debouncedUserSearch,
      lboSearch: debouncedLboSearch,
      bzkSearch: debouncedBzkSearch,
      phoneSearch: debouncedPhoneSearch,
      medications: selectedMedications,
      medicationMatch,
      pickupMethod: filter.pickupMethod || null,
      status: 'current',
      occurrenceStatus: filter.occurrenceStatus || null,
      startDate:
        dateFilterMode === 'activity' ? debouncedStartDateSearch || null : null,
      endDate:
        dateFilterMode === 'activity' ? debouncedEndDateSearch || null : null,
      renewStartDate:
        dateFilterMode === 'renewal' ? renewStartDateSearch || null : null,
      renewEndDate:
        dateFilterMode === 'renewal' ? renewEndDateSearch || null : null,
      userBirthdaySearch: userBirthdaySearch || null,
      haveApp: filter.haveApp || null,
      isHealthBookletVerified: filter.isHealthBookletVerified || null,
    }),
    [
      currentPage,
      debouncedBzkSearch,
      debouncedEndDateSearch,
      debouncedLboSearch,
      debouncedPhoneSearch,
      debouncedSearch,
      debouncedStartDateSearch,
      debouncedUserSearch,
      dateFilterMode,
      filter.haveApp,
      filter.isHealthBookletVerified,
      filter.occurrenceStatus,
      filter.pickupMethod,
      renewEndDateSearch,
      renewStartDateSearch,
      selectedMedications,
      medicationMatch,
      userBirthdaySearch,
    ]
  );

  const loadTherapies = useCallback(async (page = currentPage) => {
    const requestId = latestTherapiesRequestRef.current + 1;
    latestTherapiesRequestRef.current = requestId;
    setListLoading(true);

    const { data, ok, message } = await fetchTherapies(
      getCurrentTherapyFilters(page)
    );

    if (requestId !== latestTherapiesRequestRef.current) {
      return;
    }

    setListLoading(false);

    if (ok && data) {
      const resolvedPage = data?.pagination?.currentPage || 1;

      setTherapies(data.therapies || []);
      if (resolvedPage !== page) {
        setCurrentPage(resolvedPage);
      }
      setTotalPages(data?.pagination?.totalPages || 1);
      setTotalResults(data?.pagination?.totalCount ?? null);
    } else if (message) {
      setMessage(message);
    }
  }, [
    currentPage,
    getCurrentTherapyFilters,
  ]);

  useEffect(() => {
    loadTherapies(currentPage);
  }, [loadTherapies, refetchTherapies]);

  // useEffect(() => {
  //   setSelectedTherapyIds([]);
  // }, [
  //   currentPage,
  //   debouncedEndDateSearch,
  //   debouncedMedicationSearch,
  //   debouncedSearch,
  //   debouncedStartDateSearch,
  //   debouncedUserSearch,
  //   filter.pickupMethod,
  // ]);

  const handleBulkExport = useCallback(async () => {
    if (!selectedTherapyIds.length) return;

    setExportLoading(true);
    try {
      const { ok, data, fileName, message } = await exportTherapiesToExcel({
        ids: selectedTherapyIds,
      });

      if (!ok || !data) {
        setMessage(message || 'Doslo je do greske pri eksportu terapija.');
        return;
      }

      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'terapije-export.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setExportLoading(false);
    }
  }, [selectedTherapyIds]);

  const handleSelectionChange = useCallback((nextIds, changedRows = []) => {
    setSelectedTherapyIds(nextIds);
    setSelectedTherapiesById((prev) => {
      const next = {};

      nextIds.forEach((id) => {
        const changedRow = changedRows.find((row) => row?._id === id);
        next[id] = changedRow || prev[id];
      });

      return next;
    });
  }, []);

  const selectedTherapies = useMemo(
    () =>
      selectedTherapyIds
        .map((id) => selectedTherapiesById[id])
        .filter(Boolean),
    [selectedTherapyIds, selectedTherapiesById]
  );

  const handleOpenBulkRenewModal = useCallback(() => {
    if (!selectedTherapyIds.length) return;

    setBulkRenewError('');
    setIsBulkRenewModalOpen(true);
  }, [selectedTherapyIds.length]);

  const handleCloseBulkRenewModal = useCallback(() => {
    if (bulkRenewLoading) return;

    setBulkRenewError('');
    setIsBulkRenewModalOpen(false);
  }, [bulkRenewLoading]);

  const handleBulkRenew = useCallback(async ({ centralDate, durationMonths }) => {
    if (!selectedTherapyIds.length) return;

    setBulkRenewLoading(true);
    setBulkRenewError('');

    try {
      const { ok, message: responseMessage } = await bulkRenewTherapies({
        ids: selectedTherapyIds,
        centralDate,
        durationMonths,
      });

      if (!ok) {
        const errorMessage =
          responseMessage || 'Doslo je do greske pri obnovi terapija.';
        setMessage(errorMessage);
        setBulkRenewError(errorMessage);
        messageApi.error(errorMessage);
        return;
      }

      const successMessage = responseMessage || 'Terapije uspesno obnovljene.';
      setMessage(successMessage);
      messageApi.success(successMessage);
      setSelectedTherapyIds([]);
      setSelectedTherapiesById({});
      setIsBulkRenewModalOpen(false);
      await loadTherapies();
    } finally {
      setBulkRenewLoading(false);
    }
  }, [loadTherapies, messageApi, selectedTherapyIds]);

  const replaceOccurrenceInTherapies = useCallback((updatedOccurrence) => {
    if (!updatedOccurrence?._id) return;

    setTherapies((prev) =>
      prev.map((therapy) => ({
        ...therapy,
        highlightedOccurrence:
          therapy?.highlightedOccurrence?._id === updatedOccurrence._id
            ? { ...therapy.highlightedOccurrence, ...updatedOccurrence }
            : therapy?.highlightedOccurrence,
        occurrences: Array.isArray(therapy?.occurrences)
          ? therapy.occurrences.map((occurrence) =>
              occurrence?._id === updatedOccurrence._id
                ? { ...occurrence, ...updatedOccurrence }
                : occurrence
            )
          : therapy?.occurrences,
      }))
    );
  }, []);

  const handleOccurrenceNotificationChange = useCallback(
    async (occurrence, notificationStatus) => {
      if (!occurrence?._id) return;

      setNotificationUpdatingIds((prev) => [...new Set([...prev, occurrence._id])]);

      try {
        const { data, ok, message: responseMessage } =
          await updateTherapyOccurrenceNotification(occurrence._id, {
            notificationStatus,
          });

        if (!ok) {
          const errorMessage =
            responseMessage || 'Doslo je do greske pri azuriranju obavestenja.';
          setMessage(errorMessage);
          messageApi.error(errorMessage);
          return;
        }

        const updatedOccurrence =
          data?.occurrence || data?.data?.occurrence || data?.data || null;

        if (updatedOccurrence) {
          replaceOccurrenceInTherapies(updatedOccurrence);
        }

        messageApi.success(responseMessage || 'Obavestenje je azurirano.');
      } finally {
        setNotificationUpdatingIds((prev) =>
          prev.filter((id) => id !== occurrence._id)
        );
      }
    },
    [messageApi, replaceOccurrenceInTherapies]
  );

  const handleSelectAllTherapies = useCallback(
    async (checked) => {
      if (!checked) {
        setSelectedTherapyIds([]);
        setSelectedTherapiesById({});
        return;
      }

      setSelectAllLoading(true);
      try {
        // Jedan lak zahtev (id + ime pacijenta) sa istim filterima kao lista,
        // umesto ucitavanja svih strana liste.
        const {
          data,
          ok,
          message: selectionMessage,
        } = await fetchTherapySelection(getCurrentTherapyFilters());

        if (!ok || !data) {
          setMessage(
            selectionMessage || 'Doslo je do greske pri selektovanju terapija.'
          );
          return;
        }

        const allTherapies = data.therapies || [];
        const allIds = allTherapies
          .map((therapy) => therapy?._id)
          .filter(Boolean);

        setSelectedTherapyIds([...new Set(allIds)]);
        setSelectedTherapiesById(
          allTherapies.reduce((acc, therapy) => {
            if (therapy?._id) {
              acc[therapy._id] = therapy;
            }
            return acc;
          }, {})
        );
      } finally {
        setSelectAllLoading(false);
      }
    },
    [getCurrentTherapyFilters]
  );

  const handleOpenStatusModal = (therapy) => {
    setSelectedTherapy(therapy);
    setIsStatusModalOpen(true);
  };

  const handleCloseStatusModal = () => {
    setIsStatusModalOpen(false);
  };

  const handleOpenReminderModal = useCallback((therapy) => {
    setSelectedTherapyForReminder(therapy);
    setIsReminderModalOpen(true);
  }, []);

  const handleCloseReminderModal = () => {
    setIsReminderModalOpen(false);
  };

  const handleOpenOccurrencesModal = useCallback(async (therapy) => {
    if (!therapy) return;

    setOccurrencesLoadingTherapyId(therapy?._id || null);

    try {
      if (!therapy?.userId) {
        setSelectedTherapyForOccurrences(therapy);
        setIsOccurrencesModalOpen(true);
        return;
      }

      const { data, ok, message } = await fetchUserTherapyModalContext(
        therapy.userId
      );

      if (!ok) {
        setMessage(
          message || 'Doslo je do greske pri dobavljanju konteksta terapije.'
        );
        const therapyWithReservation =
          await loadTherapyContextWithReservation(therapy);
        setSelectedTherapyForOccurrences(therapyWithReservation || therapy);
        setIsOccurrencesModalOpen(true);
        return;
      }

      const therapyModalContext = getTherapyModalContextFromResponse(data);

      const enrichedTherapyContext = await loadTherapyContextWithReservation(
        therapyModalContext || therapy
      );

      setSelectedTherapyForOccurrences(enrichedTherapyContext || therapy);
      setIsOccurrencesModalOpen(true);
    } finally {
      setOccurrencesLoadingTherapyId(null);
    }
  }, [loadTherapyContextWithReservation]);

  const handleCloseOccurrencesModal = () => {
    setIsOccurrencesModalOpen(false);
    setSelectedTherapyForOccurrences(null);
  };

  const handleReloadTherapyContext = useCallback(async (therapyContext) => {
    if (!therapyContext?.userId) return null;

    const { data, ok } = await fetchUserTherapyModalContext(
      therapyContext.userId
    );

    if (!ok) return null;

    const refreshedTherapyContext = getTherapyModalContextFromResponse(data);
    if (!refreshedTherapyContext) return null;

    const enrichedTherapyContext = await loadTherapyContextWithReservation(
      refreshedTherapyContext
    );

    setSelectedTherapyForOccurrences(enrichedTherapyContext);
    return enrichedTherapyContext;
  }, [loadTherapyContextWithReservation]);

  const handleOpenRenewModal = useCallback((therapy) => {
    setSelectedTherapyForRenew(therapy);
    setIsRenewModalOpen(true);
  }, []);

  const handleCloseRenewModal = () => {
    setIsRenewModalOpen(false);
  };

  const handleOpenAddAcuteTherapy = useCallback((therapy) => {
    setUserForAcuteTherapyCreate({
      _id: therapy?.userId,
      userName: therapy?.userName,
      firstName: therapy?.firstName,
      lastName: therapy?.lastName,
      email: therapy?.userEmail || therapy?.email,
      phone: therapy?.userPhone || therapy?.phone,
    });
    setIsAcuteTherapyCreateOpen(true);
  }, []);

  const handleCloseAddAcuteTherapy = () => {
    setIsAcuteTherapyCreateOpen(false);
    setUserForAcuteTherapyCreate(null);
  };

  const handleFilterChange = (newFilter) => {
    setCurrentPage(1);
    setFilter((prev) => ({ ...prev, ...newFilter }));
  };

  const handleResetFilters = useCallback(() => {
    setSearch('');
    resetTableSearch();
    setSelectedMedications([]);
    setMedicationMatch(DEFAULT_MEDICATION_MATCH);
    setStartDateSearch('');
    setEndDateSearch('');
    setRenewStartDateSearch('');
    setRenewEndDateSearch('');
    setDateFilterMode('activity');
    setUserBirthdaySearch('');
    setBirthdayPickerOpen(false);
    setBirthdayDraft({ day: '', month: '', year: '' });
    setFilter({});
    setSelectedTherapyIds([]);
    setSelectedTherapiesById({});
    setCurrentPage(1);
  }, [resetTableSearch]);

  const hasActiveFilters = Boolean(
    tableSearch.isActive ||
      selectedMedications.length ||
      (dateFilterMode === 'activity'
        ? startDateSearch || endDateSearch
        : renewStartDateSearch || renewEndDateSearch) ||
      userBirthdaySearch ||
      filter.pickupMethod ||
      filter.haveApp ||
      filter.isHealthBookletVerified ||
      filter.occurrenceStatus
  );

  const pickupMethodFilterSections = useMemo(
    () => [
      {
        title: '',
        type: 'pickupMethod',
        buttonLabel: 'Preuzimanje',
        multiSelect: true,
        popupClassName: 'pickup-method-filter-dropdown',
        items: userPickupMethodOptions
          .filter(
            (option) =>
              !['APO/AKS/BEX'].includes(option.value)
          )
          .map((option) => ({
            key: option.value,
            label: option.label,
          })),
      },
    ],
    []
  );

  const appFilterSections = useMemo(
    () => [
      {
        title: '',
        type: 'haveApp',
        buttonLabel: 'App',
        layoutClassName: 'therapyFilterApp',
        items: [
          {
            key: 'true',
            label: 'Da',
          },
          {
            key: 'false',
            label: 'Ne',
          },
        ],
      },
    ],
    []
  );

  const occurrenceStatusFilterSections = useMemo(
    () => [
      {
        title: '',
        type: 'occurrenceStatus',
        buttonLabel: 'Aktivnost',
        multiSelect: true,
        // Za sada samo "Nedodeljeno"; struktura je multiSelect da se lako
        // dodaju drugi statusi instance (salju se comma-separated na backend).
        items: [
          {
            key: 'unassigned',
            label: 'Nedodeljeno',
          },
        ],
      },
    ],
    []
  );

  const healthBookletVerifiedFilterSections = useMemo(
    () => [
      {
        title: '',
        type: 'isHealthBookletVerified',
        buttonLabel: 'Overena ZK',
        items: [
          {
            key: 'true',
            label: 'Da',
          },
          {
            key: 'false',
            label: 'Ne',
          },
        ],
      },
    ],
    []
  );

  // useMemo: stabilne akcije da memoizovani redovi tabele ne bi renderovali
  // ponovo pri svakoj promeni stanja stranice.
  const canManage = isStaff(user);
  const canAdmin = isAdmin(user);
  const therapyActions = useMemo(() => [
    {
      key: 'instances',
      label: 'Prikazi aktivnosti',
      icon: (
        <img
          src="/assets/icons/view-icon.svg"
          alt="Instances"
          width={16}
          height={16}
          className="dropdown-icon"
        />
      ),
      onClick: (row) => handleOpenOccurrencesModal(row),
      disabled: (row) => occurrencesLoadingTherapyId === row?._id,
      visible: () => canManage,
    },
    {
      key: 'reminder',
      label: 'Posalji podsetnik',
      icon: (
        <img
          src="/assets/icons/mail.svg"
          alt="Notify"
          width={16}
          height={16}
          className="dropdown-icon"
        />
      ),
      onClick: (row) => handleOpenReminderModal(row),
      visible: () => canAdmin,
    },
    // {
    //   key: 'status',
    //   label: 'Izmeni terapiju',
    //   icon: (
    //     <img
    //       src="/assets/icons/edit-icon.svg"
    //       alt="Izmeni"
    //       width={16}
    //       height={16}
    //       className="dropdown-icon"
    //     />
    //   ),
    //   onClick: (row) => handleOpenStatusModal(row),
    //   visible: () =>
    //     canManage,
    // },
    {
      key: 'add-acute-therapy',
      label: 'Dodaj akutnu terapiju',
      icon: (
        <span
          aria-hidden="true"
          className="dropdown-icon"
          style={{
            display: 'inline-flex',
            width: 16,
            height: 16,
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          +
        </span>
      ),
      onClick: (row) => handleOpenAddAcuteTherapy(row),
      visible: () => canAdmin,
    },
    {
      key: 'renew',
      label: 'Obnovi terapiju',
      icon: (
        <img
          src="/assets/icons/add.svg"
          alt="Renew"
          width={16}
          height={16}
          className="dropdown-icon"
        />
      ),
      onClick: (row) => handleOpenRenewModal(row),
      visible: () => canManage,
    },
    {
      key: 'postal-form',
      label: 'Obrazac za poštu',
      icon: (
        <img
          src="/assets/icons/download.svg"
          alt="Postal form"
          width={16}
          height={16}
          className="dropdown-icon"
        />
      ),
      onClick: (row) => openPostalFormForUserId(row?.userId),
      visible: () => canManage,
    },
  ], [
    handleOpenAddAcuteTherapy,
    handleOpenOccurrencesModal,
    handleOpenReminderModal,
    handleOpenRenewModal,
    occurrencesLoadingTherapyId,
    openPostalFormForUserId,
    canManage,
    canAdmin,
  ]);

  const activeDateRange =
    dateFilterMode === 'activity'
      ? [startDateSearch, endDateSearch]
      : [renewStartDateSearch, renewEndDateSearch];

  const handleDateRangeChange = useCallback(
    (startDate, endDate) => {
      if (dateFilterMode === 'activity') {
        setStartDateSearch(startDate);
        setEndDateSearch(endDate);
        return;
      }

      setRenewStartDateSearch(startDate);
      setRenewEndDateSearch(endDate);
    },
    [dateFilterMode]
  );

  const handleDateFilterModeChange = useCallback(
    (nextMode) => {
      const [currentStartDate, currentEndDate] = activeDateRange;

      setStartDateSearch(currentStartDate || '');
      setEndDateSearch(currentEndDate || '');
      setRenewStartDateSearch(currentStartDate || '');
      setRenewEndDateSearch(currentEndDate || '');
      setDateFilterMode(nextMode);
      setCurrentPage(1);
    },
    [activeDateRange]
  );

  const therapyDateFilter = useMemo(
    () => ({
      type: 'custom',
      wide: true,
      className: 'therapyFilterActivity',
      render: () => (
        <div className="therapy-date-filter-inline">
          <Radio.Group
            className="therapy-date-filter-mode"
            size="small"
            value={dateFilterMode}
            onChange={(event) => handleDateFilterModeChange(event.target.value)}
          >
            <Radio value="activity">Mes.akt.</Radio>
            <Radio value="renewal">Obnova</Radio>
          </Radio.Group>
          <DatePicker.RangePicker
            {...fixedBottomDatePickerProps}
            placeholder={['Datum od', 'Datum do']}
            className="search-input"
            variant="borderless"
            allowClear
            allowEmpty={[true, true]}
            format="DD.MM.YYYY"
            value={[
              activeDateRange[0] ? dayjs(activeDateRange[0]) : null,
              activeDateRange[1] ? dayjs(activeDateRange[1]) : null,
            ]}
            onChange={(dates) =>
              handleDateRangeChange(
                dates?.[0] ? dates[0].format('YYYY-MM-DD') : '',
                dates?.[1] ? dates[1].format('YYYY-MM-DD') : ''
              )
            }
          />
        </div>
      ),
    }),
    [activeDateRange, dateFilterMode, handleDateRangeChange]
  );

  const formatBirthdayParts = useCallback((dateValue) => {
    if (!dateValue) {
      return { day: '', month: '', year: '' };
    }

    const parsedDate = dayjs(dateValue);

    if (!parsedDate.isValid()) {
      return { day: '', month: '', year: '' };
    }

    return {
      day: parsedDate.format('DD'),
      month: parsedDate.format('MM'),
      year: parsedDate.format('YYYY'),
    };
  }, []);

  const updateBirthdayDraftField = useCallback((field, nextValue) => {
    const maxLengths = {
      day: 2,
      month: 2,
      year: 4,
    };

    setBirthdayDraft((prev) => ({
      ...prev,
      [field]: nextValue.replace(/\D/g, '').slice(0, maxLengths[field]),
    }));
  }, []);

  const buildBirthdayIso = useCallback((draftValue) => {
    const normalizedDay = draftValue.day ? draftValue.day.padStart(2, '0') : '';
    const normalizedMonth = draftValue.month
      ? draftValue.month.padStart(2, '0')
      : '';
    const { year } = draftValue;

    if (!normalizedDay && !normalizedMonth && !year) {
      return '';
    }

    if (
      !normalizedDay ||
      !normalizedMonth ||
      year.length !== 4 ||
      normalizedDay.length !== 2 ||
      normalizedMonth.length !== 2
    ) {
      return null;
    }

    const dayNumber = Number(normalizedDay);
    const monthNumber = Number(normalizedMonth);
    const yearNumber = Number(year);

    if (
      dayNumber < 1 ||
      dayNumber > 31 ||
      monthNumber < 1 ||
      monthNumber > 12 ||
      yearNumber < 1900 ||
      yearNumber > 2100
    ) {
      return null;
    }

    const parsedDate = new Date(yearNumber, monthNumber - 1, dayNumber);

    if (
      parsedDate.getFullYear() !== yearNumber ||
      parsedDate.getMonth() !== monthNumber - 1 ||
      parsedDate.getDate() !== dayNumber
    ) {
      return null;
    }

    return `${year}-${normalizedMonth}-${normalizedDay}`;
  }, []);

  const birthdayDisplayValue = useMemo(() => {
    if (!userBirthdaySearch) {
      return 'Datum rodjenja';
    }

    const parsedDate = dayjs(userBirthdaySearch);
    return parsedDate.isValid()
      ? parsedDate.format('DD.MM.YYYY')
      : 'Datum rodjenja';
  }, [userBirthdaySearch]);

  const canApplyBirthdayFilter = useMemo(() => {
    return buildBirthdayIso(birthdayDraft) !== null;
  }, [birthdayDraft, buildBirthdayIso]);

  const handleBirthdayPickerOpenChange = useCallback(
    (open) => {
      setBirthdayPickerOpen(open);

      if (open) {
        setBirthdayDraft(formatBirthdayParts(userBirthdaySearch));
      }
    },
    [formatBirthdayParts, userBirthdaySearch]
  );

  const handleApplyBirthdayFilter = useCallback(() => {
    const nextBirthdayValue = buildBirthdayIso(birthdayDraft);

    if (nextBirthdayValue === null) {
      return;
    }

    setUserBirthdaySearch(nextBirthdayValue);
    setCurrentPage(1);
    setBirthdayPickerOpen(false);
  }, [birthdayDraft, buildBirthdayIso]);

  const handleClearBirthdayDraft = useCallback(() => {
    setBirthdayDraft({ day: '', month: '', year: '' });
    setUserBirthdaySearch('');
    setCurrentPage(1);
    setBirthdayPickerOpen(false);
  }, []);

  const handleClearBirthdayFilter = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setBirthdayDraft({ day: '', month: '', year: '' });
    setUserBirthdaySearch('');
    setCurrentPage(1);
    setBirthdayPickerOpen(false);
  }, []);

  const birthdayPopoverContent = useMemo(
    () => (
      <div className="therapy-birthday-picker">
        <div className="therapy-birthday-picker__fields">
          <Input
            className="therapy-birthday-picker__field therapy-birthday-picker__field--day"
            value={birthdayDraft.day}
            placeholder="Dan"
            inputMode="numeric"
            maxLength={2}
            onChange={(event) =>
              updateBirthdayDraftField('day', event.target.value)
            }
          />
          <Input
            className="therapy-birthday-picker__field therapy-birthday-picker__field--month"
            value={birthdayDraft.month}
            placeholder="Mesec"
            inputMode="numeric"
            maxLength={2}
            onChange={(event) =>
              updateBirthdayDraftField('month', event.target.value)
            }
          />
          <Input
            className="therapy-birthday-picker__field therapy-birthday-picker__field--year"
            value={birthdayDraft.year}
            placeholder="Godina"
            inputMode="numeric"
            maxLength={4}
            onChange={(event) =>
              updateBirthdayDraftField('year', event.target.value)
            }
          />
        </div>
        <div className="therapy-birthday-picker__actions">
          <Button type="text" size="small" onClick={handleClearBirthdayDraft}>
            Ocisti
          </Button>
          <Button
            type="primary"
            size="small"
            disabled={!canApplyBirthdayFilter}
            onClick={handleApplyBirthdayFilter}
          >
            Primeni
          </Button>
        </div>
      </div>
    ),
    [
      birthdayDraft.day,
      birthdayDraft.month,
      birthdayDraft.year,
      canApplyBirthdayFilter,
      handleApplyBirthdayFilter,
      handleClearBirthdayDraft,
      updateBirthdayDraftField,
    ]
  );

  const birthdayFilter = useMemo(
    () => ({
      type: 'custom',
      className: 'therapyFilterDob',
      render: () => (
        <Popover
          trigger="click"
          placement="bottomLeft"
          open={birthdayPickerOpen}
          onOpenChange={handleBirthdayPickerOpenChange}
          content={birthdayPopoverContent}
          classNames={{ root: 'therapy-birthday-picker-popover' }}
        >
          <button type="button" className="therapy-birthday-picker-trigger">
            <span
              className={`therapy-birthday-picker-trigger__label${
                userBirthdaySearch ? ' is-filled' : ''
              }`}
            >
              {birthdayDisplayValue}
            </span>
            {userBirthdaySearch ? (
              <span
                role="button"
                tabIndex={0}
                className="therapy-birthday-picker-trigger__clear"
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={handleClearBirthdayFilter}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    handleClearBirthdayFilter(event);
                  }
                }}
              >
                <CloseCircleFilled />
              </span>
            ) : null}
          </button>
        </Popover>
      ),
    }),
    [
      birthdayDisplayValue,
      birthdayPickerOpen,
      birthdayPopoverContent,
      handleClearBirthdayFilter,
      handleBirthdayPickerOpenChange,
      userBirthdaySearch,
    ]
  );

  return (
    <>
      <ContentLayout>
        <TableSection
          // Za loader tabele (da li jos nema redova).
          data={therapies}
          showFilter
          showCreateButton={false}
          searchFieldOrder={[
            'tableSearch',
            'birthdayDateSearch',
            'filter',
            'tertiarySearch',
            'quaternarySearch',
          ]}
          inlineHeaderActions={
            hasActiveFilters ? (
              <Button
                type="default"
                className="btn-reset-filters"
                onClick={handleResetFilters}
              >
                <span className="btn-reset-filters__content">
                  Ponisti filtere
                  <CloseOutlined />
                </span>
              </Button>
            ) : null
          }
          onSearch={setSearch}
          tableSearch={tableSearch}
          tertiarySearch={{
            type: 'custom',
            value: selectedMedications,
            onChange: setSelectedMedications,
            className: 'therapyFilterMedication',
            render: () => (
              <MedicationFilterSelect
                value={selectedMedications}
                onChange={setSelectedMedications}
                match={medicationMatch}
                onMatchChange={setMedicationMatch}
              />
            ),
          }}
          quaternarySearch={therapyDateFilter}
          birthdayDateSearch={birthdayFilter}
          filter={{
            selected: filter,
            sections: [
              pickupMethodFilterSections,
              appFilterSections,
              healthBookletVerifiedFilterSections,
              occurrenceStatusFilterSections,
            ],
            layoutClassName: 'therapyFilterPickup',
          }}
          onFilterChange={handleFilterChange}
          selectable={isStaff(user)}
          selectedRowKeys={selectedTherapyIds}
          onSelectionChange={handleSelectionChange}
          customTable={
            <TherapiesExpandableTable
              data={therapies}
              actions={therapyActions}
              selectable={isStaff(user)}
              selectedRowKeys={selectedTherapyIds}
              onSelectionChange={handleSelectionChange}
              onSelectAllChange={handleSelectAllTherapies}
              selectAllLoading={selectAllLoading}
              notificationUpdatingIds={notificationUpdatingIds}
              onNotificationChange={handleOccurrenceNotificationChange}
              emptyState={THERAPIES_EMPTY_STATE}
              handleViewUserDetails={handleViewUserDetails}
              debouncedStartDateSearch={debouncedStartDateSearch}
              tableMinWidth={1400}
            />
          }
          bulkActionContent={
            <>
              <Button
                type="default"
                loading={exportLoading}
                disabled={selectAllLoading || bulkRenewLoading}
                onClick={handleBulkExport}
              >
                Eksportuj u Excel
              </Button>
              <Button
                type="primary"
                loading={bulkRenewLoading}
                disabled={selectAllLoading || exportLoading}
                onClick={handleOpenBulkRenewModal}
              >
                {`Obnovi terapije (${selectedTherapyIds.length})`}
              </Button>
            </>
          }
          showPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalResults={totalResults}
          paginationLoading={listLoading}
          onPageChange={setCurrentPage}
          searchPlaceholder="Pretrazi terapije"
        />
      </ContentLayout>
      <UpdateTherapyStatusModal
        isOpen={isStatusModalOpen}
        onClose={handleCloseStatusModal}
        therapy={selectedTherapy}
        onUpdated={loadTherapies}
      />
      <SendTherapyReminderModal
        isOpen={isReminderModalOpen}
        onClose={handleCloseReminderModal}
        therapy={selectedTherapyForReminder}
        onSent={onRefetchTherapies}
      />
      <TherapyOccurrencesModal
        isOpen={isOccurrencesModalOpen}
        onClose={handleCloseOccurrencesModal}
        therapy={selectedTherapyForOccurrences}
        onRefetchTherapies={onRefetchTherapies}
        onReloadTherapyContext={handleReloadTherapyContext}
      />
      <RenewTherapyModal
        isOpen={isRenewModalOpen}
        onClose={handleCloseRenewModal}
        therapy={selectedTherapyForRenew}
        onRenewed={loadTherapies}
      />
      <BulkRenewTherapiesModal
        isOpen={isBulkRenewModalOpen}
        onClose={handleCloseBulkRenewModal}
        therapies={selectedTherapies}
        loading={bulkRenewLoading}
        error={bulkRenewError}
        onRenew={handleBulkRenew}
      />
      <ImportTherapiesModal
        isOpen={isImportTherapiesModalOpen}
        onClose={() => setIsImportTherapiesModalOpen(false)}
        onImported={loadTherapies}
      />
      <AddTherapyModal
        isOpen={isAcuteTherapyCreateOpen}
        onClose={handleCloseAddAcuteTherapy}
        user={userForAcuteTherapyCreate}
        onCreate={loadTherapies}
        defaultIsAcute
        lockIsAcute
        defaultDurationMonths="1"
        lockDurationMonths
      />
      <ViewUserDetailsModal
        isOpen={isViewUserDetailsModalOpen}
        user={userForView}
        loading={viewUserDetailsLoading}
        onClose={handleViewUserDetailsModalClose}
        onPasswordSubmit={handleViewUserPasswordSubmit}
        onSubmit={handleViewUserDetailsSubmit}
        onRefreshUser={handleRefreshUserForView}
      />
      <PostalFormModal
        isOpen={postalForm.isOpen}
        onClose={postalForm.close}
        user={postalForm.user}
        loading={postalForm.loading}
        error={postalForm.error}
      />
      {!!message && <span style={{ display: 'none' }}>{message}</span>}
    </>
  );
}
